import { FC, useEffect, useState } from 'react';

/// material-ui
import { useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import BlockTwoToneIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
// project imports
import SubCard from 'ui-component/cards/SubCard';
import Chip from 'ui-component/extended/Chip';
import { gridSpacing } from 'store/constant';
import { ThemeMode } from 'config';

// assets
import CalendarTodayTwoToneIcon from '@mui/icons-material/CalendarTodayTwoTone';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import EmailTwoToneIcon from '@mui/icons-material/EmailTwoTone';
import PhoneAndroidTwoToneIcon from '@mui/icons-material/PhoneAndroidTwoTone';
import MainCard from 'ui-component/cards/MainCard';

import { alpha, Avatar, CircularProgress, Tooltip } from '@mui/material';
import { usePolicyStore } from 'Application/store';
import Loader from 'ui-component/Loader';
import { useParams } from 'react-router-dom';
import { GetSinglePolicyByQuoteNumberResponse, IdentificationType } from 'Application/models';
import { addCommasToNumbers, getBackgroundColor } from 'Application/utils';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import BackTo from 'Application/components/NavigateBack';

const detailsIconSX = {
    width: 15,
    height: 15,
    verticalAlign: 'text-top',
    mr: 0.5,
    mt: 0.25
};

function createData(
    nameEmail: string,
    phoneNumber: string,
    nationality: string,
    passportNumber: string,
    kraPin: string,
    dateOfBirth: string,
    gender: string,
    occupation: string,
    attachedDocuments: string
) {
    return {
        nameEmail,
        phoneNumber,
        nationality,
        passportNumber,
        kraPin,
        dateOfBirth,
        gender,
        occupation,
        attachedDocuments
    };
}

interface ViewPolicyRwandaProps {
    getSinglePolicyByQuotationNo: GetSinglePolicyByQuoteNumberResponse | null | undefined;
}

const ViewPolicyRwanda: FC<ViewPolicyRwandaProps> = () => {
    const theme = useTheme();
    const { policyNumber } = useParams(); // Extract policyNumber from the URL
    const [downloading, setDownloading] = useState<string | null>(null);

    const {
        getSinglePolicyByQuotationNo,
        isLoading: isPolicyNoLoading,
        singlepolicyByQuotatioNoDetails
    } = usePolicyStore((state) => ({
        getSinglePolicyByQuotationNo: state.getSinglePolicyByQuotationNo,
        singlepolicyByQuotatioNoDetails: state.singlepolicyByQuotatioNoDetails,
        isLoading: state.isLoading
    }));
    const {
        getDependantsDetails,
        isLoading: isDepedantsDetailsLoading,
        dependantsDetails
    } = usePolicyStore((state) => ({
        getDependantsDetails: state.getDependantsDetails,
        dependantsDetails: state.dependantsDetails,
        isLoading: state.isLoading
    }));

    useEffect(() => {
        console.log('running, .........................');
        if (policyNumber) {
            getSinglePolicyByQuotationNo(policyNumber);
            getDependantsDetails(policyNumber);
        }
    }, [policyNumber]);

    console.log(singlepolicyByQuotatioNoDetails, 'policyByQuotatioNoDetails');
    // Destructure data from singlepolicyByQuotatioNoDetails
    const {
        policyNumber: policyNo,
        issuedBy,
        policyStatus,
        policyTransactionStatus,
        expirationDate,
        createdAt,
        quotation,
        mainCustomer,
        travelPlan,
        agency,
        paymentMethod,
        amount,
        adminFee,
        basicPremium,
        vatFee,
        bookedOnEvo,
        coverEdgePolicyCode
    } = singlepolicyByQuotatioNoDetails || {};

    // Destructure the quotation and mainCustomer details for easier use
    const {
        quotationNumber,
        quotationStatus,
        travelDestinations,
        sourceLocation,
        departureDate,
        returnDate,
        createdAt: quoteCreatedAt
    } = quotation || {};
    const {
        phoneNumber,
        residenceCountry,
        emailAddress,
        firstName,
        otherNames,
        gender,
        nationality,
        identificationType,
        identificationNumber,
        dateOfBirth,
        economicSector,
        relationshipToBritam,
        occupation,
        documents,
        physicalAddress,
        residenceAddress,
        nextOfKin
    } = mainCustomer || {};
    const { addressType, residenceType, country, province, district, sector, cell, village, streetNumber, houseNumber } =
        residenceAddress || {};

    const {
        firstName: nokFirstName,
        otherNames: nokOtherNames,
        gender: nokGender,
        identificationType: nokIDType,
        identificationNumber: nokIDNumber,
        dateOfBirth: nokDOB,
        phoneNumber: nokPhone,
        emailAddress: nokEmail,
        nationality: nokNationality,
        address: nokAddress,
        relationship: nokRelationship
    } = nextOfKin || {};
    const {
        getPolicyUploadedFiles,
        isError,
        error,
        isLoading: isDownloadFileLoading
    } = usePolicyStore((state) => ({
        getPolicyUploadedFiles: state.getPolicyUploadedFiles,
        isError: state.isError,
        error: state.error,
        isLoading: state.isLoading
    }));

    const handleDownloadFiles = async (fileName: string) => {
        setDownloading(fileName);
        try {
            toast.info('Downloading Attached file...');
            const policyDocumentBlob = await getPolicyUploadedFiles(fileName);

            const url = window.URL.createObjectURL(policyDocumentBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Policy_Attached_${fileName}.pdf`;

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            toast.success(`${fileName} downloaded successfully`);
        } catch (error) {
            console.error(error);
            toast.error(`Failed to download ${fileName}`);
        } finally {
            setDownloading(null);
        }
    };
    const handleCopyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.success('Email copied to clipboard'); // Optional toast notification for feedback
    };

    if (isPolicyNoLoading && isDepedantsDetailsLoading) {
        return <Loader />;
    }

    return (
        <>
            <BackTo title={'Back To Policies'} />

            <MainCard>
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12}>
                        <SubCard
                            title="Policy Details"
                            secondary={
                                <Typography variant="subtitle1">Placed on {dayjs(createdAt || 'N/A').format('YYYY-MM-DD')}</Typography>
                            }
                        >
                            <Grid container spacing={gridSpacing}>
                                <Grid item xs={12}>
                                    <Grid container spacing={3} direction="row" alignItems="center" wrap="wrap">
                                        {/* Avatar and Name */}
                                        <Grid item>
                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                <Avatar color="primary" sx={{ fontSize: '1rem' }}>
                                                    {firstName?.charAt(0)}
                                                    {otherNames?.charAt(0)}
                                                </Avatar>
                                                <Typography variant="body2">
                                                    {firstName} {otherNames}
                                                </Typography>
                                            </Stack>
                                        </Grid>

                                        {/* Phone Number */}
                                        <Grid item>
                                            <Typography variant="body2" display="flex" alignItems="center">
                                                <PhoneAndroidTwoToneIcon sx={{ mr: 1 }} /> {phoneNumber}
                                            </Typography>
                                        </Grid>

                                        {/* Email Address */}
                                        <Grid item>
                                            <Typography variant="body2" display="flex" alignItems="center">
                                                <EmailTwoToneIcon sx={{ mr: 1 }} /> {emailAddress}
                                                <Tooltip title="Copy email">
                                                    <IconButton
                                                        onClick={() => handleCopyToClipboard(emailAddress)}
                                                        size="small"
                                                        sx={{ ml: 1 }}
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider />
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Typography variant="h4">Policy Details</Typography>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Policy No:</Typography>
                                                        <Typography variant="body2">{policyNo}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Aims Status:</Typography>

                                                        <Chip
                                                            label={policyTransactionStatus}
                                                            size="small"
                                                            sx={
                                                                policyTransactionStatus === 'DEBITED'
                                                                    ? {
                                                                          bgcolor:
                                                                              theme.palette.mode === ThemeMode.DARK
                                                                                  ? 'dark.main'
                                                                                  : alpha(theme.palette.success.light, 0.6),
                                                                          color: 'success.dark'
                                                                      }
                                                                    : {
                                                                          bgcolor:
                                                                              theme.palette.mode === ThemeMode.DARK
                                                                                  ? 'dark.main'
                                                                                  : alpha(theme.palette.primary.light, 0.6),
                                                                          color: 'primary.dark'
                                                                      }
                                                            }
                                                        />
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Created At:</Typography>
                                                        <Typography variant="body2">{dayjs(createdAt).format('YYYY-MM-DD')}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Typography variant="h4">Agency Details</Typography>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Agency Name:</Typography>
                                                        <Typography variant="body2">{agency?.name}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Issued By:</Typography>
                                                        <Typography variant="body2">{issuedBy}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Expiry Date:</Typography>
                                                        <Typography variant="body2">
                                                            {dayjs(expirationDate).format('YYYY-MM-DD')}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Typography variant="h4">Travel Plan</Typography>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Travel Plan:</Typography>
                                                        <Typography variant="body2">{travelPlan?.name}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="h4">Quotation Details</Typography>
                                    <Grid container spacing={3} sx={{ mt: '1px' }}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Quote No:</Typography>
                                                        <Typography variant="body2">{quotationNumber}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Quotation Status:</Typography>
                                                        <Chip
                                                            label={quotationStatus}
                                                            size="small"
                                                            sx={
                                                                quotationStatus === 'COMPLETED'
                                                                    ? {
                                                                          bgcolor:
                                                                              theme.palette.mode === ThemeMode.DARK
                                                                                  ? 'dark.main'
                                                                                  : alpha(theme.palette.success.light, 0.6),
                                                                          color: 'success.dark'
                                                                      }
                                                                    : {
                                                                          bgcolor:
                                                                              theme.palette.mode === ThemeMode.DARK
                                                                                  ? 'dark.main'
                                                                                  : alpha(theme.palette.primary.light, 0.6),
                                                                          color: 'primary.dark'
                                                                      }
                                                            }
                                                        />
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Departure Date:</Typography>
                                                        <Typography variant="body2">{departureDate}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Return Date:</Typography>
                                                        <Typography variant="body2">{returnDate}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Travel From:</Typography>
                                                        <Typography variant="body2">{sourceLocation}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Destinations:</Typography>
                                                        <Typography variant="body2">
                                                            {travelDestinations?.map((destination, index) => (
                                                                <span key={index}>
                                                                    {destination.destinationName}
                                                                    {index < travelDestinations.length - 1 && ', '}
                                                                </span>
                                                            ))}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="h4">Customer Details</Typography>
                                    <Grid container spacing={3} sx={{ mt: '1px' }}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Date Of Birth</Typography>
                                                        <Typography variant="body2">{dateOfBirth}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Gender:</Typography>
                                                        <Chip
                                                            label={gender}
                                                            size="small"
                                                            sx={{
                                                                bgcolor:
                                                                    theme.palette.mode === ThemeMode.DARK
                                                                        ? theme.palette.dark.main
                                                                        : alpha(theme.palette.success.light, 0.6),
                                                                color: theme.palette.success.dark // Fixing color syntax
                                                            }}
                                                        />
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Nationality:</Typography>
                                                        <Typography variant="body2">{nationality}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">{identificationType}</Typography>
                                                        <Typography variant="body2">{identificationNumber}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Residence Country:</Typography>
                                                        <Typography variant="body2">{residenceCountry}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">occupation:</Typography>
                                                        {<Typography variant="body2">{occupation?.description}</Typography>}
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Policy Holder Relationship To Britam :</Typography>
                                                        <Typography variant="body2">{relationshipToBritam}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">EconomicSector:</Typography>
                                                        <Typography variant="body2">{economicSector?.description}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Attached Documents:</Typography>
                                                    </Stack>

                                                    {mainCustomer?.documents?.length > 0 ? (
                                                        mainCustomer.documents.map((doc, index) => (
                                                            <Stack key={index} direction="row" spacing={1} alignItems="center">
                                                                {/* Display Document Name & Type */}
                                                                <Typography variant="body2">
                                                                    {doc.documentType}: {doc.documentName}
                                                                </Typography>

                                                                {/* Download Button */}
                                                                <Tooltip placement="top" title={`Download ${doc.documentType}`}>
                                                                    <IconButton
                                                                        color="primary"
                                                                        onClick={() => handleDownloadFiles(doc.documentName)}
                                                                        disabled={isDownloadFileLoading && downloading === doc.documentName}
                                                                        size="large"
                                                                        sx={{
                                                                            color:
                                                                                isDownloadFileLoading && downloading === doc.documentName
                                                                                    ? 'grey.400'
                                                                                    : 'orange.dark',
                                                                            borderColor: 'orange.main',
                                                                            '&:hover': {
                                                                                bgcolor: isDownloadFileLoading ? 'inherit' : 'orange.light'
                                                                            }
                                                                        }}
                                                                    >
                                                                        {isDownloadFileLoading && downloading === doc.documentName ? (
                                                                            <CircularProgress size={24} color="inherit" />
                                                                        ) : (
                                                                            <BlockTwoToneIcon sx={{ fontSize: '1.1rem' }} />
                                                                        )}
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Stack>
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2">No documents</Typography>
                                                    )}
                                                </Stack>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="h4">Next of Kin Details</Typography>

                                    <Grid container spacing={3} sx={{ mt: '1px' }}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Full Name:</Typography>
                                                        <Typography variant="body2">{`${nokFirstName ?? ''} ${nokOtherNames ?? ''}`}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Gender:</Typography>
                                                        <Typography variant="body2">{nokGender ?? 'N/A'}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Relationship:</Typography>
                                                        <Typography variant="body2">{nokRelationship ?? 'N/A'}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">{nokIDType ?? 'ID Type'}:</Typography>
                                                        <Typography variant="body2">{nokIDNumber ?? 'N/A'}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Date of Birth:</Typography>
                                                        <Typography variant="body2">{nokDOB ?? 'N/A'}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Nationality:</Typography>
                                                        <Typography variant="body2">{nokNationality ?? 'N/A'}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Phone:</Typography>
                                                        <Typography variant="body2">{nokPhone ?? 'N/A'}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Email:</Typography>
                                                        <Typography variant="body2">{nokEmail ?? 'N/A'}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Address:</Typography>
                                                        <Typography variant="body2">{nokAddress ?? 'N/A'}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h4">Residence Address Details</Typography>

                                    <Grid container spacing={3} sx={{ mt: '1px' }}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Address Type:</Typography>
                                                        <Typography variant="body2">{addressType ?? 'N/A'}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Residence Type:</Typography>
                                                        <Typography variant="body2">{residenceType ?? 'N/A'}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Country:</Typography>
                                                        <Typography variant="body2">{country ?? 'N/A'}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Province:</Typography>
                                                        <Typography variant="body2">{province?.name ?? 'N/A'}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">District:</Typography>
                                                        <Typography variant="body2">{district?.name ?? 'N/A'}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Sector:</Typography>
                                                        <Typography variant="body2">{sector?.name ?? 'N/A'}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Cell:</Typography>
                                                        <Typography variant="body2">{cell?.name ?? 'N/A'}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Village:</Typography>
                                                        <Typography variant="body2">{village?.name ?? 'N/A'}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Street Number:</Typography>
                                                        <Typography variant="body2">{streetNumber ?? 'N/A'}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">House Number:</Typography>
                                                        <Typography variant="body2">{houseNumber ?? 'N/A'}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                    <Grid item xs={12}>
                        <SubCard title="Premium Breakdown" content={false}>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <SubCard
                                        sx={{
                                            mx: 3,
                                            mb: 3,
                                            bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'primary.light'
                                        }}
                                    >
                                        <Grid container justifyContent="flex-end" spacing={gridSpacing}>
                                            <Grid item sm={6} md={4}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}></Grid>
                                                    <Grid item xs={12}>
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    Basic Premium:
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    RWF {addCommasToNumbers(basicPremium?.toString() ?? '')}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    Admin Fee:
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    RWF {addCommasToNumbers(adminFee?.toString() ?? '')}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    VAT:
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    RWF {addCommasToNumbers(vatFee?.toString() ?? '')}
                                                                </Typography>
                                                            </Grid>

                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    Total Amount Paid:
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    RWF {addCommasToNumbers(amount?.toString() ?? '')}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    Payment Method:
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    {paymentMethod}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </SubCard>
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                </Grid>
            </MainCard>
        </>
    );
};

export default ViewPolicyRwanda;
